<template>
	<div class="flex flex-col gap-8">
		<template v-if="fileUploadStore.filesToUpload?.length > 0">
			<ul class="files-to-upload">
				<FileEditItem
					v-for="pickedFile in fileUploadStore.filesToUpload"
					:key="getFileKey(pickedFile.file)"
					:file-metadata="pickedFile"
					@update:file-metadata="
						fileUploadStore.updateNameOfFileToUpload(pickedFile, $event)
					"
					@remove:file="fileUploadStore.removeFileToUpload(pickedFile)"
				/>
			</ul>
			<div id="add-files-buttons">
				<AppButton
					variant="secondary"
					loading-text="Toevoegen..."
					:loading="fileUploader.isLoading.value"
					@click="uploadFilesClicked"
				>
					Toevoegen aan dossier
				</AppButton>
			</div>
			<span v-if="fileUploader.hasFailed.value" class="error-msg">
				De bestanden konden niet geüpload worden. Probeer het op een later
				moment opnieuw.
			</span>
			<hr class="separator" />
		</template>
		<div
			v-else
			class="file-drop-outlined p-4 py-10 flex justify-center items-center border-dashed border-2 text-gray-400 rounded-sm"
		>
			<input
				:id="uploaderId"
				ref="file-picker"
				type="file"
				multiple
				class="hidden"
				@change="fileInputChanged"
			/>
			<span>
				Sleep hiernaartoe om te uploaden of
				<label
					class="link"
					:for="uploaderId"
					tabindex="0"
					@keydown.enter="filePicker?.click()"
				>
					kies een document
				</label>
			</span>
		</div>
		<FileOverviewList
			v-if="
				fileUploadStore.existingFiles &&
				fileUploadStore.existingFiles.length > 0
			"
		>
			<FileOverviewListItem
				v-for="file in fileUploadStore.existingFiles"
				:key="file.filename"
				:file="file"
				:classification="classificationsMap.get(file.id) ?? null"
				:classification-feature-enabled="classificationEnabled"
				:selected="fileUploadStore.selectedFromExistingFiles?.includes(file)"
				@click="
					(doubleClick, clickMode) => fileClicked(file, doubleClick, clickMode)
				"
			/>
		</FileOverviewList>
		<FilePreview
			v-if="previewedFile"
			:file="previewedFile"
			@hide-preview="previewedFile = null"
		/>
	</div>
	<AppCard v-if="showValues" title="DEBUG - Geselecteerde documenten">
		<pre>{{ stringifyObject(fileUploadStore.selectedFromExistingFiles) }}</pre>
	</AppCard>
</template>

<script setup lang="ts">
import type { FileClickMode } from "@/enums/FileClickMode";
import { stringifyObject } from "@/helpers/uiHelpers";
import { computed, inject, ref, useTemplateRef } from "vue";
import { useWaiter } from "@/composables/useWaiter";
import { useFileUploadStore } from "@/store/useFileUploadStore";
import { useDossierStore } from "@/store/useDossierStore";
import type { DossierDocument } from "@/composables/useLoanApplicationSortedDocuments";
import { useQuery } from "@/composables/useZero";
import { queries } from "@loanlink-nl/zero";
import { useFeatureFlag } from "@/composables/useFeatureFlag";

const classificationEnabled = useFeatureFlag(
	"document-classification-intermediary",
);

withDefaults(
	defineProps<{
		uploaderId?: string;
	}>(),
	{
		uploaderId: crypto.randomUUID(),
	},
);

const filePicker = useTemplateRef("file-picker");
const fileUploadStore = useFileUploadStore();
const dossierStore = useDossierStore();

// Eén query voor alle classificaties in dit dossier (voorkomt N+1)
const { data: allClassifications } = useQuery(() =>
	queries.dossierDocumentClassifications.forDossier({
		dossierId: dossierStore.selectedDossier?.id ?? "",
	}),
);

const classificationsMap = computed(() => {
	const map = new Map<string, { label: string; confidence: number }>();
	if (!classificationEnabled.value) return map;
	for (const c of allClassifications.value ?? []) {
		// Eerste (meest recente) per document wint — query is al gesorteerd op classifiedAt DESC
		if (!map.has(c.documentId)) {
			map.set(c.documentId, { label: c.label, confidence: c.confidence });
		}
	}
	return map;
});
const showValues = inject("DEBUG_SHOW_MODELVALUES", false);
const previewedFile = ref<DossierDocument | null>(null);

const fileUploader = useWaiter(() => fileUploadStore.uploadFiles());
const fileClicked = (
	file: DossierDocument,
	doubleClick = false,
	mode: FileClickMode,
) => {
	fileUploadStore.selectFile(file, mode);

	if (mode === "replace" && doubleClick) {
		previewedFile.value = file;
	}
};

function fileInputChanged(e: Event) {
	const filePicker = e.target as HTMLInputElement;

	const files = filePicker.files;

	if (files) {
		fileUploadStore.addFilesToFilesToUpload(files);
	}
}

async function uploadFilesClicked() {
	await fileUploader.run();
	fileUploadStore.clearFilesToUpload();
}
function getFileKey(file: File) {
	return `${file.name}|${file.size.toString()}|${file.lastModified.toString()}`;
}
</script>
